import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-4192">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4193">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-4194">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-4195">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-4196" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-4197">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-4198" />
              <p className="text-sm text-gray-600" data-id="element-4199">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-4200">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-4201" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-4202">
          {/* Quality & Metadata */}
          <div data-id="element-4203">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4204">
              <Brain className="w-3.5 h-3.5" data-id="element-4205" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-4206">
              <div className="grid grid-cols-2 gap-4" data-id="element-4207">
                <div data-id="element-4208">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4209">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-4210">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-4211">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4212">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-4213">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-4214">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-4215">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-4216">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4217">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4218">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-4219">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4220">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4221">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-4222">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4223">
              <Database className="w-3.5 h-3.5" data-id="element-4224" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-4225">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4226" />
                <span className="text-sm text-gray-600" data-id="element-4227">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-4228">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-4229">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-4230">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4231">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-4232" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-4233">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-4234">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4235" />
                  <span className="text-sm" data-id="element-4236">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-4237">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4238" />
                  <span className="text-sm" data-id="element-4239">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-4240">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4241">
              <BookOpen className="w-3.5 h-3.5" data-id="element-4242" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-4243">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4244">
                      <div data-id="element-4245">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4246">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4247">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DOK_MISSING') ? <span className="text-amber-600" data-id="element-4248">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-4249">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4250">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4251">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-4252">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_MISCONCEPTION_MISSING') ? <span className="text-amber-500" data-id="element-4253">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-4254">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-4255">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4256" />
                        <span className="text-sm text-gray-600" data-id="element-4257">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-4258">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-4259" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-4260" />}
                        <span className="text-sm text-gray-600" data-id="element-4261">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}